import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, CardText, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { inAgeRange, ProductModel, ProductVisibility } from '../../../shared/orders';
import { RegistrationInfo, RegistrationUpsert } from '../../../shared/registration/model';
import { BaseUserWithAddress, isMinorAttendee } from '../../../shared/user/base';
import { BaseCreateUser, CreateSubUser, CreateSubUserAdult } from '../../../shared/user/create';
import {
  getDefaultDate,
  Loading,
  LoadingError,
  MaterialIcon,
  RegisterSubUserInfo,
  RegTypeSelectList,
  RichMarkdownIt,
  TermsLoader,
  WizardComponent,
  WizardItem,
  WizardStatus,
} from '../../components';
import { ActionType, store, Theme } from '../../services';
import { useTranslation } from '../../translations';
import {
  displayName,
  handleUnknownOptionError,
  isLogicError,
  useConvention,
  useFetcher,
  usePreferences,
  useToggle,
  useUserSafe,
} from '../../utils';
import { captureError, LogicError } from '../../utils/errorHandling';
import { ProductPrice } from '../../utils/product';
import { RegistrationDynamicInfoForm } from './DynamicRegForm';
import { EventRegistrationComplete } from './EventRegistrationComplete';

enum RegistrationStage {
  NewUser = 1,
  PickLevel = 2,
  Registration = 3,
  Review = 4,
}

function getStatus(current: RegistrationStage, stage: RegistrationStage): WizardStatus {
  if (stage === current) {
    return WizardStatus.InProgress;
  }

  return stage < current ? WizardStatus.Completed : WizardStatus.NotStarted;
}

function getUserAge(bornAt: Date | 'child' | null, newUser?: CreateSubUser): Date | 'child' | null {
  if (bornAt === 'child' || newUser?.child) {
    return 'child';
  }

  if (newUser?.bornAt) {
    return new Date(newUser.bornAt);
  }

  return bornAt;
}

interface EventRegistrationProps {
  readonly registration?: RegistrationInfo;
  readonly targetUserId?: number;
  readonly inputUser?: BaseUserWithAddress;
  // At the moment, the 'child' value is unreachable
  // We might revisit later if we want to support registration for children
  // Without providing the user's first name/last name
  readonly bornAt: Date | 'child' | null;
}

export const EventRegistration: FC<EventRegistrationProps> = ({
  registration: initReg,
  targetUserId,
  bornAt,
  inputUser,
}) => {
  const activeUser = useUserSafe();
  const [user, setUser] = useState<CreateSubUser | undefined>();
  const [registration, setRegistration] = useState(initReg);
  const [selectedRegistrationType, setSelectedRegistrationType] = useState<number | undefined>();
  const isChildRegistration = bornAt === 'child';

  // Show the "Create new user screen" if creating a sub-account that is not a child
  const [stage, setStage] = useState<RegistrationStage>(
    !targetUserId && !isChildRegistration ? RegistrationStage.NewUser : RegistrationStage.PickLevel,
  );

  const fetcher = useFetcher(async () => {
    const [order, form] = await Promise.all([
      api.getActiveOrder(activeUser.id, true),
      api.getRegistrationForm(targetUserId ?? 0),
    ]);

    return { order, form };
  }, [activeUser.id, targetUserId]);

  useEffect(() => {
    if (fetcher.data && registration) {
      const { order, form } = fetcher.data;
      const orderItem = order.orderItems.find(
        (oi) =>
          String(oi.referenceId) === String(registration.id) &&
          form.products.find(({ id }) => id === oi.product.id),
      );

      const type = form.products.find(
        ({ id }) => id === (orderItem ? orderItem.product.id : registration.attendanceType.id),
      );

      if (type) {
        selectRegistrationType(type.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.data]);

  const newUserCallback = useCallback(
    (u: BaseCreateUser) => {
      setUser({
        ...u,
        child: isChildRegistration,
        registration: {
          attendanceTypeId: 0,
          badgeName: '',
          flags: [],
          options: {},
          badgeArtId: null,
        },
      });

      setStage(RegistrationStage.PickLevel);
    },
    [isChildRegistration],
  );

  const resetRegSelect = useCallback(() => {
    setStage(RegistrationStage.PickLevel);
  }, []);

  const selectRegistrationType = useCallback((productId: number) => {
    setSelectedRegistrationType(productId);
    setStage(RegistrationStage.Registration);
  }, []);

  const completeRegInfo = useCallback(
    async (upsert: RegistrationUpsert) => {
      let updatedTargetId = targetUserId;
      try {
        if (updatedTargetId) {
          await api.upsertRegistration(updatedTargetId, upsert);
        } else {
          const response = isChildRegistration
            ? await api.createSubUser({
                child: true,
                registration: upsert,
              })
            : await api.createSubUser({
                ...(user as CreateSubUserAdult),
                phone: (user as CreateSubUserAdult).phone || null,
                child: false,
                registration: upsert,
              });

          updatedTargetId = response.userId;
        }

        // For updating the profile picture, if it has changed
        if (activeUser.id === updatedTargetId) {
          const updatedUser = await api.getActiveUser();
          store.dispatch({
            type: ActionType.ProfileUpdate,
            user: updatedUser,
          });
        }

        const reg = await api.getUserActiveRegistration(updatedTargetId);
        setRegistration(reg);
        setStage(RegistrationStage.Review);
      } catch (error) {
        if (isLogicError(error, LogicError.UnknownProductOption)) {
          handleUnknownOptionError(error.apiResponse.errors.logic, fetcher.data?.form.options);
          return;
        }

        captureError(error as Error);
      }
    },
    [activeUser.id, fetcher.data?.form.options, isChildRegistration, targetUserId, user],
  );

  const wizardItems: WizardItem[] = useMemo(() => {
    const items: WizardItem[] = [
      {
        status: getStatus(stage, RegistrationStage.PickLevel),
        text: 'registration_type',
      },
      {
        status: getStatus(stage, RegistrationStage.Registration),
        text: 'additional_information',
      },
      {
        status: getStatus(stage, RegistrationStage.Review),
        text: 'review',
      },
    ];

    if (!targetUserId && !isChildRegistration) {
      items.unshift({
        status: getStatus(stage, RegistrationStage.NewUser),
        text: 'common.new_user',
      });
    }

    return items;
  }, [isChildRegistration, stage, targetUserId]);

  if (fetcher.loading) {
    return <Loading inline />;
  }

  if (fetcher.error) {
    return <LoadingError errorText={fetcher.error.message} />;
  }

  const { form } = fetcher.data;
  if (form.products.length === 0) {
    return <RegistrationClosed />;
  }

  const registrationType = form.products.find((t) => t.id === selectedRegistrationType);
  const validRegistrationTypes = form.products.filter(
    (rt) => selectedRegistrationType === rt.id || rt.visibility === ProductVisibility.PUBLIC,
  );

  return (
    <>
      <Row className="justify-content-center">
        <Col lg={8} xs={12}>
          <WizardComponent className="justify-content-center" items={wizardItems} />
        </Col>
      </Row>
      <br />
      {stage === RegistrationStage.NewUser && (
        <EnterSubUserDetails onComplete={newUserCallback} products={form.products} />
      )}
      {stage === RegistrationStage.PickLevel && (
        <RegTypeSelectList
          attendanceType={registrationType}
          attendanceTypes={validRegistrationTypes}
          bornAt={getUserAge(bornAt, user)}
          onSelect={selectRegistrationType}
          paidAttendanceType={
            registration?.paidOrderItem &&
            validRegistrationTypes.find(({ id }) => id === registration.paidOrderItem!.productId)
          }
          raffle={registration?.raffle}
        />
      )}
      {stage === RegistrationStage.Registration && (
        <>
          <ProductSummary
            attendanceType={registrationType!}
            isPaidAttendance={!!registration}
            onReset={resetRegSelect}
          />
          <RegistrationDynamicInfoForm
            attendanceType={registrationType!}
            onSuccess={completeRegInfo}
            regForm={form}
            registration={registration}
            user={
              inputUser ? { id: inputUser.id, profilePictureUrl: inputUser.profilePictureUrl } : {}
            }
          />
        </>
      )}
      {stage === RegistrationStage.Review && (
        <EventRegistrationComplete product={registrationType!} registration={registration} />
      )}
    </>
  );
};

const RegistrationClosed: FC = () => {
  const publicProducts = useFetcher(async () => {
    return await api.getPublicProducts();
  }, []);

  const { ts, t } = useTranslation();
  return (
    <Row className="justify-content-center" id="registrationEmpty">
      <Col lg={6} xs={12}>
        <Card className="danger">
          <CardBody className="text-center">
            <MaterialIcon large name="remove_circle" type="danger" />
            <CardText tag="div">
              <p>{ts('there_are_no_products_available_for_registration')}</p>
              <p>
                {(publicProducts.data?.length || 0) > 0
                  ? t(
                      'check_the_event_store_for_other_available_items',
                      <Link to="/store">{ts('event_store')}</Link>,
                    )
                  : ts('common.please_check_back_later')}
              </p>
            </CardText>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

interface ProductSummaryProps {
  readonly attendanceType: ProductModel;
  readonly isPaidAttendance: boolean;
  readonly onReset: () => void;
}

const ProductSummary: FC<ProductSummaryProps> = ({ attendanceType, isPaidAttendance, onReset }) => {
  const { ts } = useTranslation();
  const { theme } = usePreferences()!;
  const lightMode = useMemo(() => theme === Theme.Light, [theme]);

  return (
    <Row className="justify-content-center">
      <Col className="mb-10" lg={10} xs={12}>
        <Card className="productItem markdown-container">
          <div className="productInfo">
            <h5>{displayName(attendanceType)}</h5>
            <RichMarkdownIt
              source={attendanceType.description ?? ts('common.no_description_provided')}
            />
          </div>
          <div className="productPurchaseBar" id="productChangeBar">
            {!isPaidAttendance && <ProductPrice product={attendanceType} />}
            <Button color={lightMode ? 'secondary' : 'light'} onClick={onReset} outline>
              {ts('change')}
            </Button>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

interface EnterSubUserDetailsProps {
  readonly products: ProductModel[];
  readonly onComplete: (user: BaseCreateUser) => void;
}

const EnterSubUserDetails: FC<EnterSubUserDetailsProps> = ({ products, onComplete }) => {
  const currentUser = useUserSafe();
  const { ts } = useTranslation();
  const { startAt, childAgeThreshold } = useConvention();
  const [childVerify, toggleChildVerify] = useToggle(false);
  const [user, setUser] = useState<BaseCreateUser>({
    firstName: '',
    lastName: '',
    bornAt: getDefaultDate(),
    newsletter: false,
    addressLine1: currentUser.addressLine1,
    addressLine2: currentUser.addressLine2,
    addressState: currentUser.addressState,
    addressCity: currentUser.addressCity,
    addressCountry: currentUser.addressCountry,
    addressZipcode: currentUser.addressZipcode,
    phone: null,
  });

  const hasInfo = user.firstName && user.lastName;
  const isChild =
    !!user.bornAt && isMinorAttendee(new Date(user.bornAt), startAt, childAgeThreshold);

  const hasProduct = products.some((p) =>
    inAgeRange(p, user.bornAt ? new Date(user.bornAt) : null, startAt),
  );

  const onClick = useCallback(() => {
    onComplete(user);
  }, [onComplete, user]);

  return (
    <Row className="justify-content-center" id="registrationEmpty">
      <Col lg={9} xs={12}>
        <Card>
          <CardBody>
            <RegisterSubUserInfo setUser={setUser} user={user} />
            <hr />
            {isChild && (
              <div>
                <FormGroup check className="mt-10 start-align">
                  <Input
                    checked={childVerify}
                    id="childVerify"
                    name="childVerify"
                    onChange={toggleChildVerify}
                    type="checkbox"
                  />
                  <Label check htmlFor="childVerify">
                    <TermsLoader name="childRegistrationTerms" />
                  </Label>
                </FormGroup>
                <hr />
              </div>
            )}
            <Row className="justify-content-center">
              <Col className="mt-10" lg={6} xs={12}>
                <Button
                  block
                  color="primary"
                  disabled={(isChild && !childVerify) || !hasProduct || !hasInfo}
                  id="continueRegistration"
                  onClick={onClick}
                >
                  {ts('common.continue')}
                </Button>
                {!hasProduct && <strong>{ts('no_registration_age_range')}</strong>}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};
